import { useState, useMemo, startTransition } from 'react';
import { TabGroup as HeadlessTabGroup, TabList, TabPanels, TabPanel, Tab } from '@headlessui/react';
import { Experiment } from './Experiment';
import { SortBar } from './SortBar';
import { ExperimentPercentage } from './ExperimentPercentage';
import { useSortBar } from '../contexts/SortBarProvider';
import { useTabExperiments } from '../hooks/useTabExperiments';
import { useExperiments } from '../hooks/useExperiments';
import { sortExperiments } from '../utils/sortExperiments';

/**
 * TabGroup component is used to display a tabbed interface for managing experiments.
 * It uses the Headless UI TabGroup component to create a tabbed interface.
 *
 * As for rendering, it shows a tablist with three tabs:
 * - Active: Shows all active experiments
 * - Serving One: Shows all experiments that are currently being served to one user
 * - Unregistered: Shows all experiments that are not registered in the production.json and are not being served to any user
 *
 * It then displays the experiments in the selected tab.
 *
 * @constructor
 */
export function TabGroup() {
    const [selectedIndex, setSelectedIndex] = useState(0)
    const { experiments } = useExperiments();
    const { sortBy } = useSortBar();

    const { activeExperiments, servingOneExperiments, unregisteredExperiments } = useTabExperiments(experiments);

    const tabExperiments = useMemo(() => {
        // Active is only experiments inside the production.json
        // Serving One is experiments that are currently being served to one user
        // Unregistered is experiments that are not registered in the production.json and are not being served to any user

        // We need to filter the experiments based on the selected tab
        switch (selectedIndex) {
            case 0: return activeExperiments;
            case 1: return servingOneExperiments;
            case 2: return unregisteredExperiments;
            default: return [];
        }
    }, [selectedIndex, activeExperiments, servingOneExperiments, unregisteredExperiments]);

    const sortedTabExperiments = useMemo(() => sortExperiments(tabExperiments, sortBy), [tabExperiments, sortBy]);

    // Calculate the total number of experiments
    const unusedCount = servingOneExperiments.length + unregisteredExperiments.length;
    const totalExperiments = activeExperiments.length + servingOneExperiments.length + unregisteredExperiments.length;

    const tabData = [
        { name: 'Active', count: activeExperiments.length, description: 'Experiments currently active in production' },
        { name: 'Serving One', count: servingOneExperiments.length, description: 'Experiments currently only only serving one group. Every user has the same experiment' },
        { name: 'Unregistered', count: unregisteredExperiments.length, description: 'Experiments not registered in production and not being served to any user' },
    ];

    return (
        <HeadlessTabGroup selectedIndex={selectedIndex} onChange={(index) => {
            startTransition(() => {
                setSelectedIndex(index);
            });
        }}>
            <SortBar totalCount={totalExperiments} />

            <ExperimentPercentage unusedCount={unusedCount} totalCount={totalExperiments} />

            {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
            {/* @ts-ignore tablist is a valid role. */}
            <TabList className="lg:flex gap-3 grid grid-cols-3" aria-label="Experiment tabs" role="tablist">
                {totalExperiments > 0 && tabData.map(({name, count, description}, index) => (
                    <Tab
                        key={name}
                        aria-label={`Select ${name} experiments`}
                        title={description}
                        className={`rounded-lg px-4 py-2.5 text-sm font-semibold text-white transition-all duration-200 ${
                            selectedIndex === index
                                ? 'bg-[#404955] shadow-md shadow-black/10 border border-white/10 scale-[1.02]'
                                : 'bg-[#2a3441] cursor-pointer hover:bg-[#343d4c] hover:shadow-sm hover:scale-[1.01] border border-transparent'
                        }`}
                    >
                        {name}<span className='text-xs opacity-75'> ({count}/{totalExperiments})</span>
                    </Tab>
                ))}
            </TabList>
            <TabPanels>
                <TabPanel static>
                    <div className="mt-3 mb-5 gap-4 mx-auto w-full divide-y divide-white/5 rounded-xl stack shadow-lg shadow-black/20 border border-white/5" style={{contain: 'layout style paint'}}>
                        {sortedTabExperiments.map((exp, index) => (
                            <Experiment
                                key={exp.id}
                                experiment={exp}
                                isLuckyLast={index === sortedTabExperiments.length - 1}
                                isFirst={index === 0}
                            />
                        ))}
                    </div>
                </TabPanel>
            </TabPanels>
        </HeadlessTabGroup>
    );
}
